import type { SemanticColorToken } from '../../../themes/types';
import type { BorderColorTokens } from '../../../themes/types';
import type { CssFragment } from './index';

// ============================================================================
// Types
// ============================================================================

/** true = 'border' color, or specify a color token like 'border.subtle'. */
export type DirectionalBorderValue = true | BorderColorTokens;

export interface BoxBorderProps {
  borderColor?: SemanticColorToken;
  border?: DirectionalBorderValue;
  borderTop?: DirectionalBorderValue;
  borderBottom?: DirectionalBorderValue;
  borderLeft?: DirectionalBorderValue;
  borderRight?: DirectionalBorderValue;
}

export interface ResolvedBoxBorders {
  borderColor: string | undefined;
  borderAllColor: string | undefined;
  borderTopColor: string | undefined;
  borderBottomColor: string | undefined;
  borderLeftColor: string | undefined;
  borderRightColor: string | undefined;
}

// ============================================================================
// Border color
// ============================================================================

export const resolveBorderColor = (
  value: DirectionalBorderValue,
  getColor: (token: SemanticColorToken) => string,
): string => (value === true ? getColor('border') : getColor(value));

export const resolveBorders = (
  props: BoxBorderProps,
  getColor: (token: SemanticColorToken) => string,
  resolveDirectionalBorder: (value: DirectionalBorderValue) => string,
): ResolvedBoxBorders => ({
  borderColor: props.borderColor ? getColor(props.borderColor) : undefined,
  borderAllColor: props.border ? resolveDirectionalBorder(props.border) : undefined,
  borderTopColor: props.borderTop ? resolveDirectionalBorder(props.borderTop) : undefined,
  borderBottomColor: props.borderBottom ? resolveDirectionalBorder(props.borderBottom) : undefined,
  borderLeftColor: props.borderLeft ? resolveDirectionalBorder(props.borderLeft) : undefined,
  borderRightColor: props.borderRight ? resolveDirectionalBorder(props.borderRight) : undefined,
});

/** Returns CSS fragments for all border properties. */
export const resolveBorderCss = (
  borders: ResolvedBoxBorders,
  defaultBorderWidth: string,
  borderWidth?: number,
): CssFragment[] => [
  borders.borderAllColor ? `border: ${defaultBorderWidth} solid ${borders.borderAllColor};` : null,
  borders.borderColor ? `border-color: ${borders.borderColor};` : null,
  borderWidth !== undefined ? `border-width: ${borderWidth}px;` : null,
  borders.borderTopColor ? `border-top: ${defaultBorderWidth} solid ${borders.borderTopColor};` : null,
  borders.borderBottomColor ? `border-bottom: ${defaultBorderWidth} solid ${borders.borderBottomColor};` : null,
  borders.borderLeftColor ? `border-left: ${defaultBorderWidth} solid ${borders.borderLeftColor};` : null,
  borders.borderRightColor ? `border-right: ${defaultBorderWidth} solid ${borders.borderRightColor};` : null,
];
